import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Snackbar, Alert, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { MenuOutlined as MenuIcon } from '@mui/icons-material';
import Navigation, { OPEN_ASSISTANT_OVERLAY_EVENT, OPEN_BUILDER_OVERLAY_EVENT } from './Navigation';
import ContentWrapper from './ContentWrapper';
import { useAuth } from '../../contexts/AuthContext';
import WelcomeWizard, { OPEN_ONBOARDING_EVENT } from '../shared/WelcomeWizard';
import { colors } from '../../designTokens';
import { shouldPreserveLibraryScroll } from './libraryScrollNavigation';
import ActivationBanner from './ActivationBanner';
import { updateActivationProgress } from '../../services/api';
import { LibraryOnboardingContext, isLibraryOnboardingReady } from '../../contexts/LibraryOnboardingContext';

const PromptBuilderOverlay = lazy(() => import('../prompt-builder').then((module) => ({
  default: module.PromptBuilderOverlay,
})));
const Assistant = lazy(() => import('../freestyle'));

const Container = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeClosedLocationKey, setWelcomeClosedLocationKey] = useState(null);
  const [welcomeInstance, setWelcomeInstance] = useState(0);
  const [builderOverlayOpen, setBuilderOverlayOpen] = useState(false);
  const [builderOverlayMounted, setBuilderOverlayMounted] = useState(false);
  const [builderOverlayInstanceKey, setBuilderOverlayInstanceKey] = useState(() => Date.now());
  const [assistantOverlayOpen, setAssistantOverlayOpen] = useState(false);
  const [assistantOverlayMounted, setAssistantOverlayMounted] = useState(false);
  const [assistantLaunchRequest, setAssistantLaunchRequest] = useState(null);
  const { user, isAuthenticated, isLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mainScrollRef = useRef(null);
  const previousPathnameRef = useRef(location.pathname);
  const communityTrackedRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !location.pathname.startsWith('/community') || communityTrackedRef.current === user.id) return;
    communityTrackedRef.current = user.id;
    updateActivationProgress('explore_community', 'explored').catch(() => { communityTrackedRef.current = null; });
  }, [isAuthenticated, user?.id, location.pathname]);

  // Show welcome wizard only the first time per user
  useEffect(() => {
    if (!isAuthenticated || isLoading || !user) return;

    // Check if user has already viewed the intro wizard
    const hasSeenIntro = user.viewedHelpers?.introWizard === true;

    if (!hasSeenIntro) {
      setShowWelcome(true);
    }
  }, [isAuthenticated, isLoading, user]);

  useEffect(() => {
    const mainEl = mainScrollRef.current;
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;
    if (!mainEl) return;
    if (shouldPreserveLibraryScroll(previousPathname, location.pathname)) return;

    // Reset internal route container scroll on navigation.
    // Use a second pass after layout settles to prevent late content sizing from reintroducing offset.
    mainEl.scrollTop = 0;
    requestAnimationFrame(() => {
      mainEl.scrollTop = 0;
    });
  }, [location.pathname]);

  useEffect(() => {
    const openBuilder = () => {
      setBuilderOverlayMounted(true);
      // The overlay remains mounted while closed to preserve normal draft
      // sessions. Give each explicit global launch a fresh React instance so
      // it rehydrates from the current cache rather than stale component state.
      setBuilderOverlayInstanceKey(Date.now());
      setBuilderOverlayOpen(true);
    };
    const openAssistant = (event) => {
      if (event.detail?.intent === 'build') {
        setAssistantLaunchRequest((previous) => ({ id: (previous?.id || 0) + 1, intent: 'build' }));
      }
      setAssistantOverlayMounted(true);
      setAssistantOverlayOpen(true);
    };
    window.addEventListener(OPEN_BUILDER_OVERLAY_EVENT, openBuilder);
    const openOnboarding = () => {
      setWelcomeInstance((value) => value + 1);
      setShowWelcome(true);
    };
    window.addEventListener(OPEN_ONBOARDING_EVENT, openOnboarding);
    window.addEventListener(OPEN_ASSISTANT_OVERLAY_EVENT, openAssistant);
    return () => {
      window.removeEventListener(OPEN_BUILDER_OVERLAY_EVENT, openBuilder);
      window.removeEventListener(OPEN_ONBOARDING_EVENT, openOnboarding);
      window.removeEventListener(OPEN_ASSISTANT_OVERLAY_EVENT, openAssistant);
    };
  }, []);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      width: '100%',
      // Scale the whole app (sidebar + content) so common laptop screens stay
      // comfortably dense while large desktop workstations do not feel shrunken.
      '--app-zoom': { xs: 1, sm: 0.8, md: 0.85, lg: 0.9 },
      '@media (min-width: 1800px) and (min-height: 900px)': {
        '--app-zoom': 1,
      },
      '@media (min-width: 2000px) and (min-height: 1000px)': {
        '--app-zoom': 1.08,
      },
      '@media (min-width: 2400px) and (min-height: 1200px)': {
        '--app-zoom': 1.12,
      },
      zoom: 'var(--app-zoom)',
      // CSS zoom scales vh lengths, so divide to keep the shell filling the window.
      minHeight: { xs: 'calc(100dvh / var(--app-zoom))', sm: 'calc(100vh / var(--app-zoom))' },
      height: { xs: 'calc(100dvh / var(--app-zoom))', sm: 'calc(100vh / var(--app-zoom))' },
      overflow: 'hidden',
      overflowX: 'hidden',
    }}>
      <Navigation 
        mobileOpen={mobileOpen}
        isCollapsed={isCollapsed}
        onDrawerToggle={handleDrawerToggle}
        onExpandRequest={() => setIsCollapsed(false)}
        onFeedbackSuccess={(message) => {
          setSnackbar({
            open: true,
            message,
            severity: 'success'
          });
        }}
        onFeedbackError={(message) => {
          setSnackbar({
            open: true,
            message,
            severity: 'error'
          });
        }}
      />
      <Box
        component="main"
        ref={mainScrollRef}
        sx={{
          flexGrow: 1,
          // Critical: default flex min-width is "auto" (content size), so wide tables prevent
          // shrinking and nested overflow-x: auto never gets a scrollbar. minWidth: 0 fixes that.
          minWidth: 0,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          mt: 0,
          minHeight: '100%',
          height: '100%',
          overflow: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          pl: { xs: 0, sm: 3 },
          pr: { xs: 0, sm: 2 },
          pt: { xs: '14px', sm: '16px' },
          pb: '10px',
          bgcolor: colors.contentSurface,
        }}
      >
        {isMobile && !mobileOpen && (
          <IconButton
            aria-label="open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{
              position: 'fixed',
              top: 'calc(12px + env(safe-area-inset-top, 0px))',
              right: 'calc(12px + env(safe-area-inset-right, 0px))',
              left: 'auto',
              zIndex: (t) => t.zIndex.drawer + 2,
              minWidth: 44,
              minHeight: 44,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(15,23,42,.08)',
              '&:hover': {
                bgcolor: 'grey.50',
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <ActivationBanner userId={user?.id} visitKey={location.pathname} enabled={isAuthenticated && !isLoading && !showWelcome && !assistantOverlayOpen && !builderOverlayOpen && location.pathname !== '/assistant' && location.pathname !== '/prompt-builder'} />
        <ContentWrapper>
          <LibraryOnboardingContext.Provider value={isLibraryOnboardingReady({ viewedIntro: user?.viewedHelpers?.introWizard, welcomeOpen: showWelcome, locationKey: location.key, closedLocationKey: welcomeClosedLocationKey })}>
            {children}
          </LibraryOnboardingContext.Provider>
        </ContentWrapper>
      </Box>

      {/* First-time Welcome Wizard */}
      <WelcomeWizard
        key={welcomeInstance}
        open={showWelcome}
        onClose={({ openAssistant = false, prompt = '' } = {}) => {
          setWelcomeClosedLocationKey(location.key);
          setShowWelcome(false);
          if (openAssistant && prompt) {
            window.dispatchEvent(new CustomEvent('promptcrew:onboarding-example', { detail: prompt }));
          }
          if (openAssistant && location.pathname !== '/assistant') {
            setAssistantOverlayMounted(true);
            setAssistantOverlayOpen(true);
          }
        }}
        onExplore={() => {
          setAssistantOverlayOpen(false);
          navigate('/library');
        }}
        user={user}
      />

      {builderOverlayMounted && (
        <Suspense fallback={null}>
          <PromptBuilderOverlay
            open={builderOverlayOpen}
            instanceKey={builderOverlayInstanceKey}
            initialEditData={{}}
            onClose={(savedPrompt) => {
              setBuilderOverlayOpen(false);
              if (savedPrompt?.id) {
                const promptText = savedPrompt.content || savedPrompt.prompt || '';
                const normalizedSavedPrompt = {
                  ...savedPrompt,
                  content: promptText,
                  prompt: promptText,
                  tags: savedPrompt.tags || [],
                };
                try {
                  sessionStorage.setItem('openPromptAfterSave', JSON.stringify(normalizedSavedPrompt));
                } catch {
                  /* non-critical */
                }
                navigate(`/library/${savedPrompt.id}`);
                setBuilderOverlayInstanceKey(Date.now());
              }
            }}
          />
        </Suspense>
      )}

      {assistantOverlayMounted && (
        <Suspense fallback={null}>
          <Assistant
            open={assistantOverlayOpen}
            launchRequest={assistantLaunchRequest}
            onClose={() => setAssistantOverlayOpen(false)}
          />
        </Suspense>
      )}

      {/* Snackbar for feedback notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Container; 
